import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { fetchAllPages } from '../../../../../../api/fetchAllPages';
import {
  getPoleGroupLimits,
  getPoleLimits,
  updatePoleGroupLimits,
} from '../../../../../../api/theHive/noticeCoverage';
import { PoleLimitData } from '../../../../../../api/theHive/noticeCoverage/types';
import useErrorMessage from '../../../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../../../hooks/useSnackbar';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { useFetchNoticeDetails } from '../../hooks/notices';
import { PoleLimitsTable } from './components/PoleLimitsTable';
import { PoleSummaryCard } from './components/PoleSummaryCard';
import { WaitlistPanel } from './components/WaitlistPanel';
import { Actions, Content, Header, SaveButton, SummaryGrid } from './styles';
import { GroupLimit, PoleLimit } from './types';
import { buildDraftLimits, mapGroup, mapPole } from './utils';

// A notice can have hundreds of poles (the Proto Network edital has ~500) —
// they only feed the pole picker here, but a truncated list would hide poles
// from configuration entirely, so the fan-out still loads them all.
const POLE_PAGE_SIZE = 200;
// Safety valve: caps the fan-out if the backend ever reports a huge page count.
const MAX_POLE_PAGES = 20;

// Capacity is per pair (pole × criteria group) since PROT-2950: the screen
// opens pole by pole (pole_id is required by the API) and edits the limits
// of that pole's criteria groups.
export function NoticeDetailsPoleLimits(): JSX.Element {
  const { notice } = useParams();
  const noticeId = parseInt(notice as string, 10);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const { name: noticeName, isLoading: isLoadingNotice } =
    useFetchNoticeDetails({
      noticeId,
      isNewNotice: false,
    });

  const [poles, setPoles] = useState<PoleLimit[]>([]);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isLoadingPoles, setIsLoadingPoles] = useState(true);

  const [selectedPole, setSelectedPole] = useState<PoleLimit | null>(null);
  const [groups, setGroups] = useState<GroupLimit[]>([]);
  const [draftLimits, setDraftLimits] = useState<Record<number, string>>({});
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [groupsError, setGroupsError] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [waitlistGroup, setWaitlistGroup] = useState<GroupLimit | null>(null);

  // Ignore superseded responses (fast notice/pole switch or reload after
  // save) — one counter per fetch flow.
  const polesRunRef = useRef(0);
  const runRef = useRef(0);

  const fetchPoles = useCallback(async () => {
    const runId = polesRunRef.current + 1;
    polesRunRef.current = runId;

    setIsLoadingPoles(true);
    try {
      const result = await fetchAllPages<PoleLimitData>(
        (page) => getPoleLimits(noticeId, page, POLE_PAGE_SIZE),
        { maxPages: MAX_POLE_PAGES }
      );
      if (polesRunRef.current !== runId) return;
      setPoles(result.rows.map(mapPole));
      setIsTruncated(result.truncated);
    } catch (error) {
      if (polesRunRef.current !== runId) return;
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      if (polesRunRef.current === runId) setIsLoadingPoles(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noticeId]);

  useEffect(() => {
    // a notice switch invalidates everything from the previous notice: the
    // pole picker AND the per-pair state (a save with the old ids would hit
    // another edital's poles/groups)
    runRef.current += 1;
    setSelectedPole(null);
    setGroups([]);
    setDraftLimits({});
    setWaitlistGroup(null);
    setGroupsError(false);
    if (!Number.isNaN(noticeId)) fetchPoles();
  }, [noticeId, fetchPoles]);

  const fetchGroups = useCallback(
    async (poleId: number) => {
      const runId = runRef.current + 1;
      runRef.current = runId;

      setIsLoadingGroups(true);
      setGroupsError(false);
      try {
        const response = await getPoleGroupLimits(noticeId, poleId);
        if (runRef.current !== runId) return;
        if (response instanceof Error) throw response;
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
        const mapped = (response.data?.groups ?? []).map(mapGroup);
        setGroups(mapped);
        setDraftLimits(buildDraftLimits(mapped));
      } catch (error) {
        if (runRef.current !== runId) return;
        setGroupsError(true);
        handleSnackbar(getErrorMessage(error), true);
      } finally {
        if (runRef.current === runId) setIsLoadingGroups(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noticeId]
  );

  const handleSelectPole = (pole: PoleLimit | null): void => {
    // invalidate any in-flight group fetch from the previous selection
    runRef.current += 1;
    setSelectedPole(pole);
    setGroups([]);
    setDraftLimits({});
    setWaitlistGroup(null);
    setGroupsError(false);
    if (pole) fetchGroups(pole.id);
  };

  const totals = useMemo(
    () =>
      groups.reduce(
        (summary, group) => ({
          active: summary.active + group.activeAccreditations,
          limit: summary.limit + group.limit,
          // backend available_slots also discounts pending offers, which
          // hold a slot until answered/expired (PROT-2955)
          available: summary.available + group.availableSlots,
          waitlist: summary.waitlist + group.waitlistCount,
        }),
        { active: 0, limit: 0, available: 0, waitlist: 0 }
      ),
    [groups]
  );

  const invalidGroupIds = useMemo(
    () =>
      groups
        .filter((group) => {
          const value = Number(draftLimits[group.criteriaGroupId]);
          return (
            draftLimits[group.criteriaGroupId] === '' ||
            !Number.isInteger(value) ||
            value < group.activeAccreditations
          );
        })
        .map((group) => group.criteriaGroupId),
    [draftLimits, groups]
  );

  const changedGroups = useMemo(
    () =>
      groups.filter(
        (group) => Number(draftLimits[group.criteriaGroupId]) !== group.limit
      ),
    [groups, draftLimits]
  );

  const hasChanges = changedGroups.length > 0;
  const hasErrors = invalidGroupIds.length > 0;

  const summaryCards = [
    {
      label: 'Credenciados ativos',
      value: totals.active,
      hint: `Somados em ${groups.length} grupos do polo`,
    },
    {
      label: 'Limite total do polo',
      value: totals.limit,
      hint: 'Soma dos limites por grupo',
    },
    {
      label: 'Vagas disponíveis',
      value: totals.available,
      hint: 'Descontadas as ofertas pendentes',
    },
    {
      label: 'Em fila de espera',
      value: totals.waitlist,
      hint: 'Aguardando vaga nos pares cheios',
    },
  ];

  const handleLimitChange = (criteriaGroupId: number, value: string): void => {
    if (!/^\d*$/.test(value)) return;

    setDraftLimits((current) => ({
      ...current,
      [criteriaGroupId]: value,
    }));
  };

  const handleReset = (): void => {
    setDraftLimits(buildDraftLimits(groups));
  };

  const refreshSelectedPole = useCallback(async (): Promise<void> => {
    if (selectedPole) await fetchGroups(selectedPole.id);
  }, [selectedPole, fetchGroups]);

  const handleSave = async (): Promise<void> => {
    if (hasErrors || !hasChanges || !selectedPole) return;
    setIsSaving(true);
    try {
      const response = await updatePoleGroupLimits(noticeId, {
        limits: changedGroups.map((group) => ({
          pole_id: selectedPole.id,
          criteria_group_id: group.criteriaGroupId,
          max_companies: Number(draftLimits[group.criteriaGroupId]),
        })),
      });
      if (response instanceof Error) throw response;
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      handleSnackbar('Limites atualizados com sucesso.', false);
      await fetchGroups(selectedPole.id);
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoadingNotice) {
    return <LoadingSpinner />;
  }

  return (
    <Content>
      <Header>
        <Box>
          <Typography color="text.primary" fontSize={22} fontWeight={600}>
            Limites de credenciamento por polo
          </Typography>
          <Typography color="text.secondary" mt={0.5}>
            Defina quantos credenciados ativos cada grupo de critérios pode ter
            por polo no edital{noticeName ? ` ${noticeName}` : ''}.
          </Typography>
        </Box>
      </Header>

      <Alert severity="info">
        Os limites valem por par polo × grupo de critérios. A vaga é reservada
        quando a empresa chega à fase de contrato; par sem vaga entra em fila de
        espera e vagas liberadas são ofertadas ao primeiro da fila.
      </Alert>

      {isTruncated && (
        <Alert severity="warning">
          Este edital tem mais polos do que o limite de carga da tela: o seletor
          abaixo cobre apenas os {poles.length} primeiros polos, não o edital
          inteiro.
        </Alert>
      )}

      {isLoadingPoles ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress color="secondary" />
        </Box>
      ) : (
        <>
          <Autocomplete
            getOptionLabel={(pole) =>
              `${pole.name}${pole.state ? ` (${pole.state})` : ''}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            noOptionsText="Nenhum polo encontrado"
            onChange={(_event, pole) => handleSelectPole(pole)}
            options={poles}
            renderInput={(params) => (
              <TextField
                {...params}
                color="secondary"
                label="Selecione um polo para configurar os limites"
              />
            )}
            sx={{ maxWidth: 480 }}
            value={selectedPole}
          />

          {isLoadingGroups && (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress color="secondary" />
            </Box>
          )}

          {!isLoadingGroups && selectedPole && groupsError && (
            <Alert
              action={
                <Button
                  color="inherit"
                  onClick={() => fetchGroups(selectedPole.id)}
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  Tentar novamente
                </Button>
              }
              severity="error"
            >
              Não foi possível carregar os limites deste polo.
            </Alert>
          )}

          {!isLoadingGroups &&
            selectedPole &&
            !groupsError &&
            groups.length === 0 && (
              <Alert severity="info">
                Este polo não tem grupos de critérios configuráveis.
              </Alert>
            )}

          {!isLoadingGroups && selectedPole && groups.length > 0 && (
            <>
              <SummaryGrid>
                {summaryCards.map((card) => (
                  <PoleSummaryCard
                    key={card.label}
                    label={card.label}
                    value={card.value}
                    hint={card.hint}
                  />
                ))}
              </SummaryGrid>

              <PoleLimitsTable
                groups={groups}
                draftLimits={draftLimits}
                invalidGroupIds={invalidGroupIds}
                onLimitChange={handleLimitChange}
                onOpenWaitlist={setWaitlistGroup}
              />

              {hasErrors && (
                <Alert severity="error">
                  O limite de um grupo nunca pode ser menor que a quantidade de
                  credenciados ativos do par.
                </Alert>
              )}

              <Actions>
                <Button
                  disabled={!hasChanges || isSaving}
                  onClick={handleReset}
                  sx={{ textTransform: 'none' }}
                  variant="text"
                >
                  Descartar alterações
                </Button>
                <SaveButton
                  disabled={!hasChanges || hasErrors || isSaving}
                  onClick={handleSave}
                  variant="contained"
                >
                  {isSaving ? 'Salvando...' : 'Salvar limites'}
                </SaveButton>
              </Actions>
            </>
          )}

          {selectedPole && waitlistGroup && (
            <WaitlistPanel
              // remount per pair, not gated behind isLoadingGroups (that
              // would flash the panel away on every save/offer refresh)
              key={`${selectedPole.id}-${waitlistGroup.criteriaGroupId}`}
              noticeId={noticeId}
              poleId={selectedPole.id}
              poleName={selectedPole.name}
              criteriaGroupId={waitlistGroup.criteriaGroupId}
              criteriaGroupName={waitlistGroup.criteriaGroupName}
              onClose={() => setWaitlistGroup(null)}
              onOffered={refreshSelectedPole}
            />
          )}
        </>
      )}
    </Content>
  );
}
